import React, { createContext, useContext, useState, useEffect } from 'react';
import { Platform } from 'react-native';
import { useAuth } from './AuthContext';
import { dailyDecisionService } from '../services/dailyDecisionService';

const DailyDecisionContext = createContext();

// Session storage helpers for web
const getSessionFlag = (key) => {
  if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    return sessionStorage.getItem(key) === 'true';
  }
  return false;
};

const setSessionFlag = (key, value) => {
  if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(key, value ? 'true' : 'false');
  }
};

export function DailyDecisionProvider({ children }) {
  const { user } = useAuth();
  const [showDailyDecision, setShowDailyDecision] = useState(false);
  const [todaysQuestion, setTodaysQuestion] = useState(null);
  const [hasCompletedToday, setHasCompletedToday] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewMode, setReviewMode] = useState(false);
  const [todaysAnswer, setTodaysAnswer] = useState(null);
  // Initialize from session storage to persist across page navigation within session
  const [hasBeenShownThisSession, setHasBeenShownThisSession] = useState(() => getSessionFlag('dailyDecisionShown'));
  const [hasBeenDismissedToday, setHasBeenDismissedToday] = useState(false);
  const [isInCourse, setIsInCourse] = useState(false);

  // Check daily decision status when user changes
  useEffect(() => {
    if (user?.uid) {
      checkDailyDecisionStatus();
    } else {
      setShowDailyDecision(false);
      setTodaysQuestion(null);
      setHasCompletedToday(true);
    }
  }, [user?.uid]);

  const checkDailyDecisionStatus = async () => {
    if (!user?.uid) return;
    
    setLoading(true);
    
    try {
      // Check if user has completed today's decision
      const completed = await dailyDecisionService.hasCompletedToday(user.uid);
      setHasCompletedToday(completed);
      
      if (!completed) {
        // Get today's question
        const { question, error } = await dailyDecisionService.getTodaysQuestion(user.uid);
        
        if (question && !error) {
          setTodaysQuestion(question);
          // Auto-popup on first load of session ONLY (not on every page navigation)
          const alreadyShown = getSessionFlag('dailyDecisionShown');
          console.log('Auto-popup check:', { alreadyShown, hasBeenDismissedToday, isInCourse, hasBeenShownThisSession });
          
          if (!alreadyShown && !hasBeenDismissedToday && !isInCourse && !hasBeenShownThisSession) {
            console.log('Scheduling daily decision auto-popup in 8s...');
            setTimeout(() => {
              console.log('Daily decision auto-popup triggered!');
              setShowDailyDecision(true);
              setHasBeenShownThisSession(true);
              setSessionFlag('dailyDecisionShown', true);
            }, 8000); // 8 second delay to allow welcome popup to show first
          } else {
            console.log('Auto-popup blocked - already shown this session');
          }
        } else if (error) {
          console.warn('Daily decision service error:', error);
          if (error.includes('permission')) {
            console.warn('Database permissions need to be configured for daily decisions');
          }
        }
      } else {
        // If completed, load the answer for review/tooltip purposes
        const answer = await dailyDecisionService.getTodaysAnswer(user.uid);
        setTodaysAnswer(answer);
      }
    } catch (error) {
      console.error('Error checking daily decision status:', error);
    }
    
    setLoading(false);
  };

  const completeDailyDecision = async (questionId, answerId, answerType, rskAwarded) => {
    if (!user?.uid) return { success: false };
    
    try {
      const result = await dailyDecisionService.submitAnswer(
        user.uid,
        questionId,
        answerId,
        answerType,
        rskAwarded
      );
      
      if (result.success) {
        setHasCompletedToday(true);
        // Don't close the modal here - let the component handle it after showing results
      }
      
      return result;
    } catch (error) {
      console.error('Error completing daily decision:', error);
      return { success: false, error: error.message };
    }
  };

  const closeDailyDecision = () => {
    setShowDailyDecision(false);
    setReviewMode(false);
    
    // Track dismissal to prevent auto-popup until next day
    if (!hasCompletedToday) {
      setHasBeenDismissedToday(true);
    }
    
    // Don't immediately check status - wait a bit to prevent flash
    // and only if admin was testing (not if they completed legitimately)
    if (user?.userType === 'admin' && !hasCompletedToday) {
      setTimeout(() => {
      checkDailyDecisionStatus();
      }, 500);
    }
  };

  const openDailyDecision = async () => {
    // If already completed today, open in review mode instead
    if (hasCompletedToday) {
      console.log('Daily decision already completed, opening review mode');
      openReviewMode();
      return;
    }
    
    setReviewMode(false);
    if (todaysQuestion) {
      setShowDailyDecision(true);
    } else {
      // Get today's question if we don't have it
      const { question } = await dailyDecisionService.getTodaysQuestion(user.uid);
      if (question) {
        setTodaysQuestion(question);
        setShowDailyDecision(true);
      }
    }
  };

  const openReviewMode = async () => {
    console.log('openReviewMode called:', { hasCompletedToday, user: !!user });
    if (hasCompletedToday && user) {
      console.log('Fetching today\'s answer for review...');
      // Get today's answer for review
      const answer = await dailyDecisionService.getTodaysAnswer(user.uid);
      console.log('Today\'s answer:', answer);
      
      if (answer) {
        // Clear any existing state first
        setShowDailyDecision(false);
        setTodaysQuestion(null);
        
        // Set review state
        setTodaysAnswer(answer);
        setReviewMode(true);
        
        // Show modal after state is set
        setTimeout(() => {
          setShowDailyDecision(true);
        }, 100);
        
        console.log('Review mode set, should show popup. State will be:', {
          reviewMode: true,
          showDailyDecision: true,
          hasAnswer: !!answer
        });
      } else {
        console.error('No answer data received for review mode');
      }
    } else {
      console.warn('Cannot open review mode:', { hasCompletedToday, hasUser: !!user });
    }
  };

  // Force show daily decision (for testing - admin only)
  const forceShowDailyDecision = async () => {
    if (!user?.uid) {
      console.warn('No user ID available for daily decision');
      return;
    }
    
    // Check if user is admin (only admins should use this function)
    if (user.userType !== 'admin') {
      console.warn('Force show daily decision is only available for admin users');
      return;
    }
    
    try {
      console.log('[DailyDecision] Getting today\'s question...');
      
      // Reset states first to ensure clean slate
      setShowDailyDecision(false);
      setReviewMode(false);
      setTodaysAnswer(null);
      setHasCompletedToday(false); // Temporarily set to false for testing
      
      const { question, error } = await dailyDecisionService.getTodaysQuestion(user.uid);
      
      console.log('[DailyDecision] Service response:', { question, error });
      
      if (error) {
        console.error('Error forcing daily decision:', error);
        alert(`Error loading daily decision: ${error}`);
        return;
      }
      
      if (question) {
        console.log('[DailyDecision] Setting question and showing popup');
        setTodaysQuestion(question);
        
        // Show modal after state is properly set
        setTimeout(() => {
          setShowDailyDecision(true);
        }, 100);
        
        console.log('Daily decision popup forced for admin testing');
      } else {
        console.warn('No question received from service');
        alert('No question available. Check console for details.');
      }
    } catch (error) {
      console.error('Error in forceShowDailyDecision:', error);
      alert(`Unexpected error: ${error.message}`);
    }
  };

  // Function to set course engagement status (call this when entering/exiting courses)
  const setCourseEngagement = (engaged) => {
    setIsInCourse(engaged);
  };

  // Reset dismissal state at midnight (new day)
  useEffect(() => {
    const checkNewDay = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      
      const msUntilMidnight = tomorrow.getTime() - now.getTime();
      
      setTimeout(() => {
        setHasBeenDismissedToday(false);
        // Recheck daily decision status for new day
        if (user?.uid) {
          checkDailyDecisionStatus();
        }
      }, msUntilMidnight);
    };
    
    checkNewDay();
  }, [user?.uid]);

  return (
    <DailyDecisionContext.Provider value={{
      showDailyDecision,
      todaysQuestion,
      hasCompletedToday,
      loading,
      reviewMode,
      todaysAnswer,
      hasBeenDismissedToday,
      isInCourse,
      completeDailyDecision,
      closeDailyDecision,
      openDailyDecision,
      openReviewMode,
      forceShowDailyDecision,
      checkDailyDecisionStatus,
      setCourseEngagement,
    }}>
      {children}
    </DailyDecisionContext.Provider>
  );
}

export function useDailyDecision() {
  const context = useContext(DailyDecisionContext);
  if (context === undefined) {
    throw new Error('useDailyDecision must be used within a DailyDecisionProvider');
  }
  return context;
}